// Chemin: C:\gestion-copro\src\components\layout\AdminSaasLayout.tsx
// Layout pour les pages Admin SaaS (Dominique / DOM TECH & SERVICES)
// Session 54 : Création initiale
// Session 148 : Ajout entrée "Utilisateurs VIGIE" dans le menu
// Session 149 : Suppression bouton "Interface client" (obsolète en multi-copro)
// Session 187 : Ajout entrée "CRM — Prospection" dans le menu
// Session 224 : Ajout entrée "Commissions" dans le menu
// Session 239 : Ajout entrée "Démos" dans le menu

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  Database, 
  Settings, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Shield,
  Users,
  MessageSquare,
  Target,
  Coins,
  Play,
  Trash2,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface AdminSaasLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
}

// ============================================
// NAVIGATION ADMIN SAAS
// ============================================

const navItems: NavItem[] = [
  { 
    path: '/admin-saas', 
    label: 'Tableau de bord', 
    icon: <LayoutDashboard size={20} />,
    description: 'Vue globale multi-copropriétés'
  },
  { 
    path: '/admin-saas/utilisateurs-vigie', 
    label: 'Utilisateurs VIGIE', 
    icon: <Users size={20} />,
    description: 'Gérer les abonnés VIGIE'
  },
  { 
    path: '/admin-saas/crm', 
    label: 'CRM — Prospection', 
    icon: <Target size={20} />,
    description: 'Pipeline et tunnel de vente'
  },
  { 
    path: '/admin-saas/commissions', 
    label: 'Commissions', 
    icon: <Coins size={20} />,
    description: 'Versements apporteurs'
  },
  // Session 239 : Démos commerciales premium
  { 
    path: '/admin-saas/demos', 
    label: 'Démos', 
    icon: <Play size={20} />,
    description: 'Comptes de démonstration'
  },
  // Session 255 : Nettoyage GED
  { 
    path: '/admin-saas/nettoyage-ged', 
    label: 'Nettoyage GED', 
    icon: <Trash2 size={20} />,
    description: 'Supprimer les documents zombies'
  },
  { 
    path: '/admin-saas/feedback', 
    label: 'Feedback', 
    icon: <MessageSquare size={20} />,
    description: 'Avis et suggestions'
  },
  { 
    path: '/admin-saas/coproprietes', 
    label: 'Copropriétés', 
    icon: <Building2 size={20} />,
    description: 'Gérer les copropriétés clientes'
  },
  { 
    path: '/admin-saas/sauvegardes', 
    label: 'Sauvegardes', 
    icon: <Database size={20} />,
    description: 'Backups Firebase'
  },
  { 
    path: '/admin-saas/config', 
    label: 'Configuration', 
    icon: <Settings size={20} />,
    description: 'Paramètres SENTINELLE'
  },
  { 
    path: '/admin-saas/stats', 
    label: 'Statistiques', 
    icon: <BarChart3 size={20} />,
    description: 'Utilisation plateforme'
  },
];

// ============================================
// COMPOSANT
// ============================================

const AdminSaasLayout: React.FC<AdminSaasLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { userProfile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header Admin SaaS - Couleur distincte (slate au lieu de blue) */}
      <header className="bg-slate-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Menu burger (mobile) */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo Admin SaaS */}
          <Link to="/admin-saas" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Shield size={24} className="text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-lg">Copro Protect</span>
              <span className="ml-2 px-2 py-0.5 bg-emerald-500 text-xs rounded-full font-medium">
                Admin SaaS
              </span>
            </div>
          </Link>

          {/* Actions droite */}
          <div className="flex items-center gap-4">
            {/* Profil */}
            <div className="relative group">
              <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-700 transition-colors">
                <span className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center font-semibold">
                  {userProfile?.prenom?.charAt(0) || 'D'}
                </span>
                <span className="hidden sm:inline text-sm">
                  {userProfile?.prenom || 'Admin'}
                </span>
              </button>
              
              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-3 border-b border-gray-100">
                  <p className="font-semibold text-gray-800">
                    {userProfile?.prenom} {userProfile?.nom}
                  </p>
                  <p className="text-xs text-gray-500">{userProfile?.email}</p>
                  <p className="text-xs text-emerald-600 font-medium mt-1">
                    Administrateur SaaS
                  </p>
                </div>
                <div className="p-2">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  >
                    <LogOut size={16} />
                    <span>Déconnexion</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-[60px] left-0 bottom-0 w-72 bg-white shadow-lg z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Titre section */}
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Administration plateforme
          </h2>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className={isActive ? 'text-emerald-600' : 'text-gray-400'}>
                  {item.icon}
                </span>
                <div className="flex flex-col">
                  <span>{item.label}</span>
                  {item.description && (
                    <span className="text-xs text-gray-400 font-normal">
                      {item.description}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Séparateur */}
        <div className="mx-4 my-4 border-t border-gray-100" />

        {/* Info version */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500">
              <span className="font-semibold">Copro Protect</span> v1.0
            </p>
            <p className="text-xs text-slate-400 mt-1">
              DOM TECH & SERVICES
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <main className="lg:ml-72 pt-[60px] min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminSaasLayout;
